/**
 * polling — the shared cached-polling state machine for the trial-visualization
 * read API. Story C builds the Run Overview on it; Story D reuses it verbatim for
 * the Run Board.
 *
 * The transport is ETag-conditional polling (design spec "Liveness via cached
 * polling"): each poll sends `If-None-Match` with the last ETag and the daemon
 * answers `304 Not Modified` when nothing changed, so an idle board transfers
 * almost nothing. This module is the pure reducer over poll outcomes — kept free
 * of React and timers so the transitions are tested deterministically; the
 * `usePolling` hook is the thin React shell that drives it.
 */

import type { ReadApiError } from './client'

/** Relaxed default poll cadence (ms) — within the spec's 2–5s window. */
export const DEFAULT_POLL_INTERVAL_MS = 3_000

/** The outcome of one conditional fetch: fresh body, unchanged (304), or failure. */
export interface TaggedResult<T> {
  status: 'ok' | 'not-modified'
  /** The ETag to send on the next poll, or null when the server set none. */
  etag: string | null
  /** The decoded body on `ok`; null on `not-modified`. */
  data: T | null
}

/** A conditional fetcher: given the last ETag, resolve a {@link TaggedResult} or throw {@link ReadApiError}. */
export type TaggedFetcher<T> = (etag: string | null) => Promise<TaggedResult<T>>

/** The observable polling state the UI renders from. */
export interface PollingState<T> {
  /** The last good payload, retained across `304`s and failures so the board never blanks. */
  data: T | null
  /** The last failure, retained while `failing` so the UI can name the endpoint; cleared on success. */
  error: ReadApiError | null
  /** Epoch ms of the last successful poll (200 or 304) — drives `updated Ns ago`. */
  lastSuccessAt: number | null
  /** True after at least one failure since the last success — drives the disconnected indicator. */
  failing: boolean
}

export const INITIAL_POLLING_STATE: PollingState<never> = {
  data: null,
  error: null,
  lastSuccessAt: null,
  failing: false,
}

/** A single poll outcome fed into the reducer. */
export type PollEvent<T> =
  | { type: 'ok'; data: T | null; at: number }
  | { type: 'not-modified'; at: number }
  | { type: 'error'; error: ReadApiError; at: number }

/**
 * Fold a poll outcome into the polling state. A success (200/304) clears the
 * error and marks the connection live; a failure retains the last good data and
 * flips `failing` so the UI ages honestly to disconnected rather than blanking.
 */
export function nextPollingState<T>(prev: PollingState<T>, event: PollEvent<T>): PollingState<T> {
  switch (event.type) {
    case 'ok':
      return { data: event.data ?? prev.data, error: null, lastSuccessAt: event.at, failing: false }
    case 'not-modified':
      return { ...prev, error: null, lastSuccessAt: event.at, failing: false }
    case 'error':
      return { ...prev, error: event.error, failing: true }
  }
}
